import { useState } from 'react';

export default function PricingSection() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  const plans = [
    {
      name: 'ライトプラン',
      description: '小規模物件向け',
      monthlyPrice: '29,800',
      yearlyPrice: '298,000',
      features: [
        '1物件まで対応',
        '予約管理・カレンダー同期',
        'ゲスト対応代行（日本語のみ）',
        '清掃手配（別途実費）',
        '月次レポート',
        'メールサポート',
      ],
      highlight: false,
      color: 'from-stone-100 to-amber-50',
    },
    {
      name: 'スタンダードプラン',
      description: '最も人気のプラン',
      monthlyPrice: '49,800',
      yearlyPrice: '498,000',
      features: [
        '3物件まで対応',
        '予約管理・カレンダー同期',
        'ゲスト対応代行（多言語対応）',
        '清掃手配（別途実費）',
        '価格最適化・収益分析',
        '月次レポート＋改善提案',
        '電話・チャットサポート',
        'トラブル対応24時間',
      ],
      highlight: true,
      color: 'from-[#8b2500] via-[#a52a00] to-[#8b2500]',
    },
    {
      name: 'プレミアムプラン',
      description: '複数物件・本格運営向け',
      monthlyPrice: '89,800',
      yearlyPrice: '898,000',
      features: [
        '無制限物件対応',
        '予約管理・カレンダー同期',
        'ゲスト対応代行（多言語対応）',
        '清掃手配（別途実費）',
        '価格最適化・収益分析',
        '月次レポート＋改善提案',
        '専任担当者アサイン',
        'トラブル対応24時間',
        '写真撮影・リスティング作成',
        '税務相談サポート',
      ],
      highlight: false,
      color: 'from-stone-100 to-amber-50',
    },
  ];

  return (
    <section id="pricing" className="py-24 relative overflow-hidden">
      {/* 和紙風背景 */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-stone-50 to-amber-50" />
      
      {/* 上部装飾ライン */}
      <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-r from-transparent via-[#8b2500] to-transparent opacity-60" />
      <div className="absolute top-3 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />
      
      {/* 市松模様装飾 */}
      <div className="absolute top-8 left-0 right-0 flex justify-center gap-1">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={`w-3 h-3 ${i % 2 === 0 ? 'bg-[#8b2500]' : 'bg-[#c9a84c]'} opacity-40`}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            <span className="text-[#8b2500]">◆</span> 料金プラン <span className="text-[#8b2500]">◆</span>
          </h2>
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#c9a84c]" />
            <span className="text-[#c9a84c] text-sm" style={{ fontFamily: "'Noto Serif JP', serif" }}>✦</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#c9a84c]" />
          </div>
          <p className="text-lg text-slate-600 mb-8" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            物件数や運営規模に応じて、最適なプランをお選びいただけます
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-4 bg-white border-2 border-[#8b2500] rounded-full p-1.5 shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full font-bold text-sm transition-all whitespace-nowrap cursor-pointer ${
                billingCycle === 'monthly'
                  ? 'bg-gradient-to-r from-[#8b2500] to-[#a52a00] text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              月額払い
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-full font-bold text-sm transition-all whitespace-nowrap cursor-pointer ${
                billingCycle === 'yearly'
                  ? 'bg-gradient-to-r from-[#8b2500] to-[#a52a00] text-white shadow-md'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              年額払い <span className="text-[#c9a84c] ml-1">（2ヶ月分お得）</span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {plans.map((plan, idx) => (
            <div
              key={idx}
              className={`relative rounded-2xl overflow-hidden transition-all hover:scale-105 ${
                plan.highlight
                  ? 'shadow-2xl ring-4 ring-[#8b2500]/30'
                  : 'shadow-lg hover:shadow-xl'
              }`}
            >
              {/* 四隅装飾 */}
              <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#8b2500]" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#8b2500]" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#8b2500]" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#8b2500]" />

              {plan.highlight && (
                <div className="absolute -top-1 -right-1 bg-gradient-to-r from-[#c9a84c] to-[#d4af37] text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl shadow-lg" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  <span className="mr-1">✦</span>人気No.1
                </div>
              )}

              <div className={`bg-gradient-to-br ${plan.color} p-8`}>
                <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  {plan.name}
                </h3>
                <p className="text-sm text-slate-600 mb-6" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  {plan.description}
                </p>
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className={`text-5xl font-extrabold ${plan.highlight ? 'text-white' : 'text-[#8b2500]'}`} style={{ fontFamily: "'Noto Serif JP', serif" }}>
                      ¥{billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                    </span>
                    <span className={`text-sm ${plan.highlight ? 'text-white/80' : 'text-slate-500'}`} style={{ fontFamily: "'Noto Serif JP', serif" }}>
                      / {billingCycle === 'monthly' ? '月' : '年'}
                    </span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <p className={`text-xs mt-2 ${plan.highlight ? 'text-white/70' : 'text-slate-500'}`} style={{ fontFamily: "'Noto Serif JP', serif" }}>
                      月額換算: ¥{Math.floor(parseInt(plan.yearlyPrice.replace(/,/g, '')) / 12).toLocaleString()}
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className={`flex-shrink-0 w-5 h-5 flex items-center justify-center ${plan.highlight ? 'text-[#c9a84c]' : 'text-[#8b2500]'} text-sm`}>
                        ✦
                      </span>
                      <span className={`text-sm ${plan.highlight ? 'text-white' : 'text-slate-700'}`} style={{ fontFamily: "'Noto Serif JP', serif" }}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  className={`w-full py-4 rounded-lg font-bold transition-all hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap cursor-pointer ${
                    plan.highlight
                      ? 'bg-white text-[#8b2500] hover:bg-amber-50'
                      : 'bg-gradient-to-r from-[#8b2500] to-[#a52a00] text-white hover:from-[#a52a00] hover:to-[#8b2500]'
                  }`}
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  <span className="mr-2">✦</span>
                  このプランを選ぶ
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <div className="bg-white border-2 border-[#8b2500] rounded-xl p-6 max-w-3xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#8b2500] to-[#a52a00] rounded-lg flex-shrink-0">
              <i className="ri-information-line text-white text-xl"></i>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                料金について
              </h4>
              <ul className="space-y-1 text-sm text-slate-600" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                <li className="flex items-center gap-2">
                  <span className="text-[#c9a84c]">✦</span>
                  清掃費用は別途実費（1回あたり5,000円〜）
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#c9a84c]">✦</span>
                  初期費用・解約金は一切不要
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#c9a84c]">✦</span>
                  プラン変更はいつでも可能
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 下部装飾ライン */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={`w-3 h-3 ${i % 2 === 0 ? 'bg-[#8b2500]' : 'bg-[#c9a84c]'} opacity-40`}
          />
        ))}
      </div>
      <div className="absolute bottom-4 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-r from-transparent via-[#8b2500] to-transparent opacity-60" />
    </section>
  );
}